import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { ScrollText, Download, Loader2, RefreshCw, Search, X } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { listAuditEvents, auditFacets, exportAuditEventsCsv } from "@/lib/audit.functions";

export const Route = createFileRoute("/_authenticated/app/settings/audit-log")({
  head: () => ({
    meta: [
      { title: "Audit log — CairnOS" },
      { name: "description", content: "Tamper-evident record of every operational change." },
    ],
  }),
  component: AuditLog,
});

function ActionBadge({ action }: { action: string }) {
  const map: Record<string, string> = {
    create: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    insert: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    update: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    delete: "bg-destructive/10 text-destructive",
    archive: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  };
  const cls =
    map[action.toLowerCase()] ??
    (action.includes("delete") || action.includes("revoke")
      ? "bg-destructive/10 text-destructive"
      : action.includes("create") || action.includes("issue")
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : "bg-muted text-muted-foreground");
  return <Badge variant="outline" className={`font-mono text-[10px] ${cls}`}>{action}</Badge>;
}

function AuditLog() {
  const { tenantId } = useScope();

  const listFn = useServerFn(listAuditEvents);
  const facetsFn = useServerFn(auditFacets);
  const exportFn = useServerFn(exportAuditEventsCsv);

  const [entityType, setEntityType] = useState("all");
  const [action, setAction] = useState("all");
  const [actorId, setActorId] = useState("all");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [inspecting, setInspecting] = useState<any>(null);

  const facetsQ = useQuery({
    queryKey: ["audit-facets", tenantId],
    enabled: !!tenantId,
    queryFn: () => facetsFn({ data: { tenantId: tenantId! } }),
    staleTime: 60_000,
  });

  const filters = useMemo(
    () => ({
      tenantId: tenantId!,
      entityType: entityType === "all" ? null : entityType,
      action: action === "all" ? null : action,
      actorId: actorId === "all" ? null : actorId,
      search: search.trim() || null,
      fromIso: fromDate ? new Date(fromDate).toISOString() : null,
      toIso: toDate ? new Date(toDate + "T23:59:59").toISOString() : null,
      limit: 200,
    }),
    [tenantId, entityType, action, actorId, search, fromDate, toDate],
  );

  const eventsQ = useQuery({
    queryKey: ["audit-events", filters],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: filters }),
  });

  const exportMut = useMutation({
    mutationFn: () => exportFn({ data: { ...filters, limit: 10000 } }),
    onSuccess: (res) => {
      const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.count} events`);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const rows = eventsQ.data?.rows ?? [];
  const facets = facetsQ.data ?? { entityTypes: [], actions: [], actors: [] };

  const clearAll = () => {
    setEntityType("all"); setAction("all"); setActorId("all");
    setSearch(""); setFromDate(""); setToDate("");
  };
  const hasFilters = entityType !== "all" || action !== "all" || actorId !== "all" || !!search || !!fromDate || !!toDate;

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ScrollText className="h-6 w-6" /> Audit log
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every actor-attributable change to operational data. Tenant-scoped, manager-only,
            tamper-evident.
          </p>
        </div>
        <Button variant="outline" onClick={() => exportMut.mutate()} disabled={exportMut.isPending}>
          {exportMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Filters</CardTitle>
          <CardDescription>Drawn from the last 2,000 events for instant scoping.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search action, entity id, or diff…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={entityType} onValueChange={setEntityType}>
              <SelectTrigger><SelectValue placeholder="Entity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All entities</SelectItem>
                {facets.entityTypes.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.value} <span className="text-muted-foreground">({f.count})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger><SelectValue placeholder="Action" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {facets.actions.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.value} <span className="text-muted-foreground">({f.count})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
            <div>
              {eventsQ.isFetching ? "Loading…" : `${rows.length} event${rows.length === 1 ? "" : "s"}`}
              {rows.length === 200 && " (cap reached, narrow filters)"}
            </div>
            <div className="flex gap-2">
              {hasFilters && (
                <Button size="sm" variant="ghost" onClick={clearAll}>
                  <X className="h-3 w-3 mr-1" /> Clear
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => eventsQ.refetch()}>
                <RefreshCw className={`h-3 w-3 mr-1 ${eventsQ.isFetching ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {eventsQ.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-sm text-muted-foreground text-center">
              No events match these filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[170px]">When</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Entity ID</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Changes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r: any) => {
                  const diffKeys = r.diff && typeof r.diff === "object" ? Object.keys(r.diff) : [];
                  return (
                    <TableRow
                      key={r.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setInspecting(r)}
                    >
                      <TableCell className="text-xs text-muted-foreground">
                        <div title={format(new Date(r.created_at), "PPpp")}>
                          {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                        </div>
                      </TableCell>
                      <TableCell><ActionBadge action={r.action} /></TableCell>
                      <TableCell className="font-mono text-xs">{r.entity_type}</TableCell>
                      <TableCell className="font-mono text-[10px] text-muted-foreground">
                        {r.entity_id.slice(0, 8)}…
                      </TableCell>
                      <TableCell className="font-mono text-[10px] text-muted-foreground">
                        {r.actor_id ? r.actor_id.slice(0, 8) + "…" : <span className="italic">system</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate">
                        {diffKeys.length > 0 ? diffKeys.slice(0, 5).join(", ") : "—"}
                        {diffKeys.length > 5 && ` +${diffKeys.length - 5}`}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!inspecting} onOpenChange={(o) => !o && setInspecting(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ActionBadge action={inspecting?.action ?? ""} />
              <span className="font-mono text-sm">{inspecting?.entity_type}</span>
            </SheetTitle>
            <SheetDescription className="font-mono text-xs">
              {inspecting?.entity_id}
            </SheetDescription>
          </SheetHeader>
          {inspecting && (
            <div className="mt-4 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-muted-foreground">When</div>
                  <div>{format(new Date(inspecting.created_at), "PPpp")}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Actor</div>
                  <div className="font-mono text-xs">{inspecting.actor_id ?? "system"}</div>
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-2">Diff</div>
                {inspecting.diff && typeof inspecting.diff === "object" ? (
                  <DiffView diff={inspecting.diff} />
                ) : (
                  <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">{JSON.stringify(inspecting.diff, null, 2)}</pre>
                )}
              </div>
              <div className="border-t pt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setEntityType(inspecting.entity_type); setInspecting(null); }}
                >
                  Filter by {inspecting.entity_type}
                </Button>
                {inspecting.actor_id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setActorId(inspecting.actor_id); setInspecting(null); }}
                  >
                    Filter by this actor
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setSearch(inspecting.entity_id); setInspecting(null); }}
                >
                  Trace this entity
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DiffView({ diff }: { diff: Record<string, any> }) {
  const entries = Object.entries(diff);
  return (
    <div className="border rounded divide-y text-xs">
      {entries.map(([key, val]) => {
        const isOldNew = val && typeof val === "object" && ("old" in val || "new" in val);
        return (
          <div key={key} className="p-2 grid grid-cols-[120px_1fr] gap-2">
            <div className="font-mono text-muted-foreground">{key}</div>
            {isOldNew ? (
              <div className="space-y-1">
                {"old" in val && (
                  <div className="flex gap-2">
                    <span className="text-destructive">−</span>
                    <code className="break-all">{JSON.stringify(val.old)}</code>
                  </div>
                )}
                {"new" in val && (
                  <div className="flex gap-2">
                    <span className="text-emerald-600">+</span>
                    <code className="break-all">{JSON.stringify(val.new)}</code>
                  </div>
                )}
              </div>
            ) : (
              <code className="break-all">{JSON.stringify(val)}</code>
            )}
          </div>
        );
      })}
    </div>
  );
}